import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, ArrowRight, BookOpen, Clock, Sparkles } from "lucide-react";
import AppNav from "@/components/app/AppNav";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

/**
 * Informed OS Academy — landing / catalog page (Phase 1b).
 *
 * Route: `/academy`.
 *
 * Fetches `GET /api/academy/programs`. When the backend returns 404
 * with `feature_disabled`, we render a soft "Coming soon" surface —
 * this happens whenever `academy_enabled=false`, which is the
 * expected default state until the operator flips the launch flag.
 */
export default function AcademyLanding() {
  const { user } = useAuth();
  const [state, setState] = useState({ status: "loading", items: [], error: null, disabled: false });

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, status: "loading", error: null }));
    try {
      const res = await api.get("/academy/programs");
      setState({ status: "ready", items: res.data?.items || [], error: null, disabled: false });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (err?.response?.status === 404 && code === "feature_disabled") {
        setState({ status: "disabled", items: [], error: null, disabled: true });
        return;
      }
      setState({ status: "error", items: [], error: formatApiError(err), disabled: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div
      data-testid="academy-landing"
      className="min-h-screen bg-v2-background text-v2-text"
    >
      <AppNav />
      <main
        data-testid="academy-landing-main"
        className="max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-24"
      >
        <header className="mb-12">
          <div
            data-testid="academy-landing-eyebrow"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border"
            style={{
              background: "var(--v2-accent-soft)",
              borderColor: "var(--v2-accent)",
              color: "var(--v2-accent)",
            }}
          >
            <GraduationCap className="w-3.5 h-3.5" />
            Informed OS Academy
          </div>
          <h1
            className="mt-6 text-[36px] md:text-[48px] font-semibold tracking-tight text-v2-text"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
            data-testid="academy-landing-title"
          >
            Executive certificate programs.
          </h1>
          <p
            className="mt-4 max-w-2xl text-[15px] md:text-[16px] text-v2-text-muted leading-relaxed"
            data-testid="academy-landing-subtitle"
          >
            Structured programs in Business Acquisition, SBA Funding Readiness,
            Government Contracting, and Nonprofit Operations. Choose a program
            to see the curriculum and enroll.
          </p>
        </header>

        {user?.team_role === "owner" ? (
          <Link
            to="/academy/ask"
            data-testid="academy-owner-ask-card"
            className="group block mb-10 rounded-xl border border-v2-border bg-v2-surface p-6 transition-all hover:shadow-lg hover:border-v2-accent"
          >
            <div className="flex items-start gap-4">
              <span
                className="inline-flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
                style={{ background: "var(--v2-accent-soft)", color: "var(--v2-accent)" }}
              >
                <Sparkles className="w-5 h-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3
                    className="text-[18px] md:text-[20px] font-semibold tracking-tight text-v2-text"
                    style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
                  >
                    Ask Informed
                  </h3>
                  <span
                    data-testid="academy-owner-ask-card-badge"
                    className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border border-v2-accent text-v2-accent"
                    style={{ background: "var(--v2-accent-soft)" }}
                  >
                    Owner access
                  </span>
                </div>
                <p className="mt-2 text-sm text-v2-text-muted">
                  Ask Informed is available to the account owner. Live AI
                  responses are temporarily unavailable pending provider
                  verification, and no approved Academy course material has
                  been published yet.
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-v2-text-muted group-hover:text-v2-accent flex-shrink-0" />
            </div>
          </Link>
        ) : null}

        {state.status === "loading" ? (
          <div data-testid="academy-landing-loading" className="text-v2-text-muted">Loading programs…</div>
        ) : null}

        {state.status === "disabled" ? (
          <AcademyDisabledState />
        ) : null}

        {state.status === "error" ? (
          <div
            data-testid="academy-landing-error"
            className="rounded-xl border border-[var(--v2-danger)] bg-[var(--v2-surface)] p-6 text-v2-text"
          >
            <p className="font-semibold text-[var(--v2-danger)]">Couldn't load programs.</p>
            <p className="mt-2 text-sm text-v2-text-muted">{state.error}</p>
            <button
              data-testid="academy-landing-retry"
              type="button"
              onClick={load}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-v2-border text-sm hover:bg-v2-surface-muted"
            >
              Retry
            </button>
          </div>
        ) : null}

        {state.status === "ready" && state.items.length === 0 ? (
          <div
            data-testid="academy-landing-empty"
            className="rounded-xl border border-v2-border bg-v2-surface p-10 text-center"
          >
            <BookOpen className="w-8 h-8 mx-auto text-v2-text-muted" />
            <p className="mt-4 text-v2-text font-semibold">No programs available yet.</p>
            <p className="mt-2 text-sm text-v2-text-muted">
              New programs are being prepared. Check back soon.
            </p>
          </div>
        ) : null}

        {state.status === "ready" && state.items.length > 0 ? (
          <ul
            data-testid="academy-landing-grid"
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {state.items.map((p) => (
              <ProgramCard key={p.id} program={p} />
            ))}
          </ul>
        ) : null}
      </main>
    </div>
  );
}

function ProgramCard({ program }) {
  return (
    <li>
      <Link
        to={`/academy/programs/${program.slug}`}
        data-testid={`academy-program-card-${program.slug}`}
        className="group block rounded-xl border border-v2-border bg-v2-surface p-6 transition-all hover:shadow-lg hover:border-v2-accent"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3
              className="text-[18px] md:text-[20px] font-semibold tracking-tight text-v2-text"
              style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
            >
              {program.title}
            </h3>
            {program.subtitle ? (
              <p className="mt-2 text-sm text-v2-text-muted line-clamp-2">{program.subtitle}</p>
            ) : null}
          </div>
          <ArrowRight className="w-5 h-5 text-v2-text-muted group-hover:text-v2-accent flex-shrink-0" />
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-v2-text-muted">
          <span className="inline-flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" />
            {program.module_count ?? 0} module{program.module_count === 1 ? "" : "s"}
          </span>
          {program.estimated_hours ? (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              ~{program.estimated_hours}h
            </span>
          ) : null}
        </div>
      </Link>
    </li>
  );
}

function AcademyDisabledState() {
  return (
    <div
      data-testid="academy-landing-disabled"
      className="rounded-xl border border-v2-border bg-v2-surface p-10 text-center"
    >
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border"
        style={{
          background: "var(--v2-accent-soft)",
          borderColor: "var(--v2-accent)",
          color: "var(--v2-accent)",
        }}
      >
        <Clock className="w-3.5 h-3.5" />
        Coming soon
      </div>
      <p className="mt-6 text-v2-text font-semibold">Informed OS Academy is launching soon.</p>
      <p className="mt-3 text-sm text-v2-text-muted max-w-md mx-auto">
        We're finalising the four foundation programs. You'll get access here
        once we go live.
      </p>
    </div>
  );
}
